import { useEffect, useState } from "react";
import { FetchDataOptions } from "modules/main/services/fetchData";
import { AuthError } from "modules/authentication/types/form-login-types";
import apiClient from "../services/apiClient";

export const useFetch = <T,>(
    url: string | null,
    options: Partial<FetchDataOptions> = {},
    force?: boolean
): { data: T | null; error: any; loading: boolean } => {
    const [data, setData] = useState<T | null>(null);
    const [error, setError] = useState<any>(null);
    const [loading, setLoading] = useState<boolean>(true);

    useEffect(() => {
        const controller = new AbortController();
        const signal = controller.signal;

        const fetchData = async () => {
            setLoading(true);
            setError(null);
            
            try {
                if (!url) { 
                    return;
                }
                const defaultOptions: FetchDataOptions = {
                    method: "GET",
                    headers: options.headers || {},
                    body: undefined,
                    credentials: "include", 
                    ...options
                };
                const hasHeaders = Boolean(defaultOptions.headers && Object.keys(defaultOptions.headers).length > 0);
                const requestConfig = hasHeaders ? { headers: defaultOptions.headers } : undefined;
                // Usar apiClient que maneja automáticamente refresh tokens
                let result: T;
                if (defaultOptions.method === "GET") {
                    result = await apiClient.get<T>(url, requestConfig);
                } else if (defaultOptions.method === "POST") {
                    result = await apiClient.post<T>(url, defaultOptions.body, requestConfig);
                } else if (defaultOptions.method === "PUT") {
                    result = await apiClient.put<T>(url, defaultOptions.body, requestConfig);
                } else if (defaultOptions.method === "DELETE") {
                    result = await apiClient.delete<T>(url, requestConfig);
                } else {
                    // Para otros métodos, usar el cliente request directo
                    result = await apiClient.request<T>(url, {
                        method: defaultOptions.method,
                        headers: defaultOptions.headers,
                        body: defaultOptions.body ? JSON.stringify(defaultOptions.body) : undefined,
                    });
                }

                // Verificar si la petición fue cancelada
                if (signal.aborted) {
                    return;
                }

                setData(result);
            } catch (error) {
                if (error instanceof Error && error.name !== "AbortError") {
                    setError(error);
                }
                if (error instanceof AuthError) {
                    setError(error);
                }
            } finally {
                if (!signal.aborted) {
                    setLoading(false);
                }
            }
        };

        fetchData();

        return () => {
            controller.abort();
        };
    }, [url, force]);

    return { data, error, loading };
};